/**
 * Stage 2 — per-column horizontal surface extraction (pure).
 *
 * A surface is a prominent local maximum of the column's z-histogram: thin in
 * z (one bin plus its neighbors) and clearly denser than the column's typical
 * occupancy. Wall columns — uniformly full over their whole height — produce
 * NO surfaces, which is intended: walls are room boundaries, not room content.
 */

import type { ColumnSurface } from './types';
import type { FloorDetectionTuning } from './tuning';

/**
 * Median over ALL bins — the column's background occupancy. Room columns are
 * mostly empty (median 0, no suppression); wall columns are occupied over
 * their whole height (median ≈ typical fill, suppressing their noise bumps).
 */
function medianOccupancy(bins: Uint16Array): number {
  const sorted = Array.from(bins).sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)];
}

/** Raw point count in the window binIndex ± 1 (clamped). */
function windowCount(bins: Uint16Array, i: number): number {
  let sum = bins[i];
  if (i > 0) sum += bins[i - 1];
  if (i < bins.length - 1) sum += bins[i + 1];
  return sum;
}

/**
 * Extract the horizontal surfaces of one column, ascending by z.
 *
 * @param bins    Per-column z-histogram (counts per bin).
 * @param binSize Bin height in metres.
 * @param z0      World z of the bottom edge of bin 0.
 */
export function extractColumnSurfaces(bins: Uint16Array, binSize: number, z0: number, tuning: FloorDetectionTuning): ColumnSurface[] {
  const n = bins.length;
  if (n === 0) return [];

  // 1-2-1 smoothing suppresses noise-split double peaks.
  const smoothed = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    smoothed[i] = (i > 0 ? bins[i - 1] : 0) + 2 * bins[i] + (i < n - 1 ? bins[i + 1] : 0);
  }

  const median = medianOccupancy(bins);
  // The CENTER bin (not the 3-bin window, which is ~3× the median in a
  // uniformly occupied column by construction) must clearly exceed the
  // column's background occupancy — this is what silences wall columns.
  const prominenceFloor = median * tuning.SURFACE_PROMINENCE_FACTOR;

  // Candidates: smoothed local maxima over a ±2 window clearing both the
  // absolute and the prominence threshold.
  const candidates: number[] = [];
  for (let i = 0; i < n; i++) {
    if (bins[i] === 0) continue;
    const v = smoothed[i];
    let isMax = true;
    for (let j = Math.max(0, i - 2); j <= Math.min(n - 1, i + 2); j++) {
      if (j === i) continue;
      if (smoothed[j] > v || (smoothed[j] === v && j < i)) {
        isMax = false;
        break;
      }
    }
    if (!isMax) continue;
    if (windowCount(bins, i) < tuning.MIN_SURFACE_POINTS) continue;
    if (bins[i] < prominenceFloor) continue;
    candidates.push(i);
  }

  // Merge candidates closer than MIN_PEAK_SEPARATION_BINS, keeping the stronger.
  const merged: number[] = [];
  for (const i of candidates) {
    const last = merged[merged.length - 1];
    if (merged.length > 0 && i - last < tuning.MIN_PEAK_SEPARATION_BINS) {
      if (windowCount(bins, i) > windowCount(bins, last)) merged[merged.length - 1] = i;
    } else {
      merged.push(i);
    }
  }

  // Weighted centroid over binIndex ± 1 gives sub-bin z precision.
  return merged.map((i) => {
    let weight = 0;
    let weightedZ = 0;
    for (let j = Math.max(0, i - 1); j <= Math.min(n - 1, i + 1); j++) {
      const binCenterZ = z0 + (j + 0.5) * binSize;
      weight += bins[j];
      weightedZ += bins[j] * binCenterZ;
    }
    return { z: weightedZ / weight, count: windowCount(bins, i), binIndex: i };
  });
}
